// ArriveInfoInput.js
import React, { useEffect, useState } from 'react';
import { Button, DialogContent, DialogActions, Grid } from '@mui/material';
import { AppWord, AppObject, AppStyle, CodeGroup } from '../../value/AppConst';
import NearMeIcon from '@mui/icons-material/NearMe';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import VzmIntField from '../../common/VzmIntField';
import VzmCheckbox from '../../common/VzmCheckbox';
import VzmSelector from '../../common/VzmSelector';
import Time24Picker from '../../common/Time24Picker';
import AutoTextField from '../../common/AutoTextField';
import { CircleLoading } from '../../common/CircleLoading';
import { ErrorHintBox, VzmPrint } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { ArriveInfoDictionary } from './ArriveInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import {
    codeTreeRepo, dcInfoListRepo, vehTypeInfoListRepo,
    storeInfoListUsingRepo, storeInfoListRepo,
    comInfoListRepo
} from '../../value/AppModel';
import ValueMisc from '../../value/ValueMisc';
import { LIST_USING_STORE_INFO } from '../store_info/StoreInfoGql';
import { LIST_VEH_TYPE_INFO } from '../veh_type_info/VehTypeInfoGql';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';

const InputMap = ArriveInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (ARRIVE_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-08-28 17:05:01 using DbdbTap
 */
export default function ArriveInfoInput({
    open,
    item,
    currentDcInfo, // {dcCd, dcName, ...}
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const dcInfoListData = useReactiveVar(dcInfoListRepo);
    const storeInfoListData = useReactiveVar(storeInfoListRepo);
    const storeInfoListUsingData = useReactiveVar(storeInfoListUsingRepo);
    const vehTypeInfoListData = useReactiveVar(vehTypeInfoListRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [isDcForFresh, setIsDcForFresh] = useState(false);
    const [freshCargoVehTypes, setFreshCargoVehTypes] = useState([]);
    const [normalVehTypes, setNormalVehTypes] = useState([]);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [dcName, setDcName] = useState(''); // only for view.
    const [storeName, setStoreName] = useState(''); // only for view.
    const [maxVehTypeName, setMaxVehTypeName] = useState(''); // only for view.
    const [mainVehTypeName, setMainVehTypeName] = useState(''); // only for view.
    const codeTree = useReactiveVar(codeTreeRepo);
    const [regTypeList, setRegTypeList] = useState([]);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    //const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    // ##### Call GraphQL to get List #####
    // use_yn=='Y'인 점포목록을 조회 {storeId, storeTypeCd, storeTypeName, storeName, useYn}
    const [getStoreInfoListUsing, responseStoreList] = useLazyQuery(LIST_USING_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
		onError: (error) => addAlert({title: "점포목록 조회 오류", message: [error.message]})
    });

    const [getVehTypeInfoList, responseVehTypeList] = useLazyQuery(LIST_VEH_TYPE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehTypeList(data, option)},
		onError: (error) => addAlert({title: '차종목록 조회 오류', message: [error.message]})
    });

    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
		onError: (error) => addAlert({title: '운송사목록 조회 오류', message: [error.message]})
    });

    const getStoreInfoListUsingOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - storeInfoListUsingData.when < 300 && storeInfoListUsingData.records.length > 0) return; // 5분 이내 조회된 데이터는 재사용.
        // 큰 데이터에 있으면 복사해 오기.
        if(now - storeInfoListData.when < 300 && storeInfoListData.records.length > 0) {
            storeInfoListUsingRepo({
                when: storeInfoListData.when,
                records: storeInfoListData.records.filter((rec)=>{
                    return rec.useYn === 'Y';
                }).map((rec)=>{
                    const newRec = {
                        storeId: rec.storeId,
                        storeTypeCd: rec.storeTypeCd,
                        storeTypeName: rec.storeTypeName,
                        storeName: rec.storeName,
                        useYn: rec.useYn
                    };
                    return newRec;
                })
            });
            return;
        }
        getStoreInfoListUsing();
    };

    const getVehTypeInfoListOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - vehTypeInfoListData.when < 600 && vehTypeInfoListData.records.length > 0) return;
        getVehTypeInfoList();
    };

    const getComInfoListOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - comInfoListData.when < 600 && comInfoListData.records.length > 0) return;
        getComInfoList();
    };   

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
                setDcName(item.dcName);
                setStoreName(item.storeName);
                setMainVehTypeName(item.mainVehTypeName);
                setMaxVehTypeName(item.maxVehTypeName);
            }
            else {
                inputSet.data = {...defaultInputData};
                inputSet.data.regTypeCd = 'M'; // manual 등록
                if(currentDcInfo) {
                    inputSet.data.dcCd = currentDcInfo.dcCd;
                    setDcName(currentDcInfo.dcName);
                }
            }
            setInput(inputSet);
            setIsEdit(editing);
            getStoreInfoListUsingOnCondition();
            getVehTypeInfoListOnCondition();
            getComInfoListOnCondition();
        }
    }, [open, item, currentDcInfo]);

    useEffect(()=>{
        setRegTypeList(ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.REG_TYPE_CD, ArriveInfoDictionary.inputMap.regTypeCd.id));
    }, [codeTree]);

    // DC is fresh or not
    useEffect(()=>{
        if(open) {
            if(dcInfoListData.records && dcInfoListData.records.length > 0 && input.data.dcCd) {

                const dc = dcInfoListData.records.find((rec)=>rec.dcCd === input.data.dcCd);
                if(dc) {
                    setIsDcForFresh(dc.dcTypeCd === 'F');
                }
                else {
                    setIsDcForFresh(false);
                }
            }
        }
    }, [open, input.data.dcCd, dcInfoListData.records]);

    // Divide vehTypes into freshCargo is true or false.
    useEffect(()=>{
        if(open) {
            const fresh = [];
            const normal = [];
            vehTypeInfoListData.records.forEach((rec)=>{
                if(rec.freshCargo) fresh.push(rec);
                else normal.push(rec);
            });
            setFreshCargoVehTypes(fresh);
            setNormalVehTypes(normal);
        }
    }, [open, vehTypeInfoListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.storeInfoListUsing) {
            storeInfoListUsingRepo({when:new Date().getTime()/1000, records:data.storeInfoListUsing});
        }
    };

    const onCompleteGetVehTypeList = (data, clientOption) => {
        if(data.vehTypeInfoList) {
            vehTypeInfoListRepo({when:new Date().getTime()/1000, records:data.vehTypeInfoList});
        }
    };

    const onCompleteGetComList = (data, clientOption) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            startIcon={<NearMeIcon fontSize={AppWord.LARGE} />}
            title={isEdit ? '출발도착정보 수정' : '출발도착정보 등록'}
        >
            <DialogContent>
                <Grid container spacing={2} style={{minWidth:800}}>
                    {
                        isEdit ?
                        <Grid item xs={12}><VzmPrint label='출발도착정보코드' value={input.data.arriveId} /></Grid>
                        : null
                    }
                    <Grid item xs={5} >
                        {
                            isEdit ?
                            <VzmTextField
                                dict={InputMap.dcName}
                                value={dcName}
                                disabled={true}
                                fullWidth={true}
                            /> :
                            <VzmSelector
                                dict={InputMap.dcCd}
                                value={input.data.dcCd}
                                onChange={onChangeValue}
                                list={dcInfoListData.records}
                                fullWidth={true}
                            />
                        }
                    </Grid>
                    <Grid item xs={2}>
                        <VzmTextField
                            dict={InputMap.dcCd}
                            value={input.data.dcCd || 'N/A'}
                            label='DC코드'
                            fullWidth={true}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={5}></Grid>

                    <Grid item xs={4}>
                        <VzmIntField
                            dict={InputMap.basicDockno}
                            value={input.data.basicDockno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={5}>
                        {
                            isEdit ?
                            <VzmTextField
                                dict={InputMap.storeName}
                                value={storeName}
                                disabled={true}
                                fullWidth={true}
                            /> :
                            <AutoTextField
                                dict={InputMap.storeId}
                                value={input.data.storeId}
                                onChange={onChangeValue}
                                onSelectRecord={(record)=>{setStoreName(record.storeName)}}
                                list={storeInfoListUsingData.records}
                            />
                        }
                    </Grid>
                    <Grid item xs={2}>
                        <VzmTextField
                            dict={InputMap.storeId}
                            value={input.data.storeId || 'N/A'}
                            label='점포코드'
                            fullWidth={true}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={5}></Grid>

                    <Grid item xs={4}>
                        <VzmIntField
                            dict={InputMap.distanceOne}
                            value={input.data.distanceOne}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmIntField
                            dict={InputMap.distanceRound}
                            value={input.data.distanceRound}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Time24Picker
                            dict={InputMap.basicExpectRunTime}
                            value={input.data.basicExpectRunTime}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <Time24Picker
                            dict={InputMap.startReqTimeToday}
                            label={InputMap.startReqTimeToday.label+'(당일)'}
                            value={input.data.startReqTimeToday}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Time24Picker
                            dict={InputMap.arriveReqTimeNext}
                            label={InputMap.arriveReqTimeNext.label+'(익일)'}
                            value={input.data.arriveReqTimeNext}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                    </Grid>

                    <Grid item xs={12}>
                        <VzmCheckbox
                            dict={InputMap.autoPlanYn}
                            label='자동배차 사용여부'
                            value={input.data.autoPlanYn}
                            onChange={onChangeValue}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <VzmSelector
                            dict={InputMap.mainVehTypeId}
                            value={input.data.mainVehTypeId}
                            onSelectRecord={(record)=>{setMainVehTypeName(record.vehTypeName)}}
                            onChange={onChangeValue}
                            list={isDcForFresh ? freshCargoVehTypes : normalVehTypes}
                            fullWidth={true}
                            disabled={input.data.autoPlanYn !== 'Y'}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmSelector
                            dict={InputMap.maxVehTypeId}
                            value={input.data.maxVehTypeId}
                            onChange={onChangeValue}
                            onSelectRecord={(record)=>{setMaxVehTypeName(record.vehTypeName)}}
                            list={isDcForFresh ? freshCargoVehTypes : normalVehTypes}
                            fullWidth={true}
                            disabled={input.data.autoPlanYn !== 'Y'}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmSelector
                            dict={InputMap.storeApplyCom}
                            value={input.data.storeApplyCom}
                            onChange={onChangeValue}
                            fullWidth={true}
                            list={comInfoListData.records.filter((rec)=>rec.dcCd === input.data.dcCd)}
                            disabled={input.data.autoPlanYn !== 'Y'}
                        />

                    </Grid>

                    <Grid item xs={4}>
                        <Time24Picker
                            dict={InputMap.firstShipTime}
                            value={input.data.firstShipTime}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Time24Picker
                            dict={InputMap.lastShipTime}
                            value={input.data.lastShipTime}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={1}>
                    </Grid>
                    <Grid item xs={3}>
                        <VzmIntField
                            dict={InputMap.loadMaxCnt}
                            value={input.data.loadMaxCnt}
                            onChange={onChangeValue}
                            fullWidth={true}
                            disabled={input.data.autoPlanYn !== 'Y'}
                        />
                    </Grid>

                </Grid>
                {/* 
                    <VzmInputBlock>
                    </VzmInputBlock> */}
                
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    responseStoreList.loading ?
                    <CircleLoading /> : null
                }
                {
                    responseVehTypeList.loading ?
                    <CircleLoading /> : null
                }
                {
                    responseComList.loading ?
                    <CircleLoading /> : null
                }
                {
                    responseSaving.loading
                    ?
                    <span>
                        <CircleLoading />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
